import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  url?: string;
  headers?: Record<string, string> | undefined;
}

type QueueItem = {
  resolve: (value: string | null) => void;
  reject: (error: Error) => void;
};

let isRefreshing = false;
let queue: QueueItem[] = [];

function processQueue(error: Error | null, token: string | null) {
  queue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  queue = [];
}

export const setupTokenRefreshInterceptor = (
  axiosInstance: AxiosInstance,
  getToken: () => string | null,
  refreshToken: () => Promise<boolean>,
  onRefreshFailed: () => void,
  options?: { skipUrls?: string[] }
) => {
  const skipUrls = options?.skipUrls ?? [
    "/v1/auth/login",
    "/v1/auth/register",
    "/v1/auth/refresh-token",
    "/v1/auth/logout",
  ];

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = (error.config ?? {}) as ExtendedAxiosRequestConfig;
      const url = originalRequest.url ?? "";

      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // защита от циклов
      if (skipUrls.some((s) => url.includes(s))) {
        return Promise.reject(error);
      }

      // защита от бесконечного ретрая
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      // если refresh уже идёт — ставим запрос в очередь
      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then((token) => {
          // важно чтобы queued-запрос тоже не ушёл в новый refresh цикл
          originalRequest._retry = true;

          originalRequest.headers = originalRequest.headers ?? {};
          if (token) originalRequest.headers["Authorization"] = `Bearer ${token}`;

          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const ok = await refreshToken();

        if (!ok) {
          const e = new Error("Refresh failed");
          processQueue(e, null);
          isRefreshing = false;
          onRefreshFailed();
          return Promise.reject(e);
        }

        const token = getToken();
        processQueue(null, token);

        originalRequest.headers = originalRequest.headers ?? {};
        if (token) originalRequest.headers["Authorization"] = `Bearer ${token}`;

        return axiosInstance(originalRequest);
      } catch (e) {
        const err = e instanceof Error ? e : new Error("Refresh failed");
        processQueue(err, null);
        isRefreshing = false;
        onRefreshFailed();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
  );
};
